import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";

import CustomDataGrid from "./components/CustomDataGrid";
import AddMaterial from "./add";
import EditMaterial from "./edit";

import useUser from "../../../../hooks/useUser";
import useAuth from "../../../../hooks/useAuth";
import { useDialog } from "../../../../contexts/DialogContext";
import { getTimeStamp } from "../../../../utils/common";

function Materials({ userId }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { openDialog } = useDialog();
  const navigate = useNavigate();

  const { getAllUserMaterial, userMaterials, paging, isLoading, downloadFile } = useUser();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const methods = useForm();

  const [selectedRows, setSelectedRows] = useState([]);
  const [sortModel, setSortModel] = useState([{ field: "updatedAt", sort: "desc" }]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  /* -------------------- PAYLOAD (MEMOIZED) -------------------- */

  const payload = useMemo(() => {
    if (!userId) return null;

    return {
      filters: [
        {
          fieldName: "radUserId",
          fieldValue: userId,
          op: "eq",
          dataType: "int",
          condition: "and",
          isJoin: true,
          attributeName: "radUser",
          entityName: "RadUserEntity",
        },
      ],
      pagination: {
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      },
      sortFields: sortModel.map((s) => ({
        field: s.field,
        sort: s.sort?.toUpperCase(),
      })),
    };
  }, [userId, paginationModel, sortModel]);

  /* -------------------- DATA FETCH -------------------- */

  useEffect(() => {
    if (!userId || !payload) return;
    getAllUserMaterial(payload);
  }, [userId, payload]);

  /* -------------------- PAGINATION META -------------------- */

  const paginationMetaRef = useRef();

  const paginationMeta = useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  /* -------------------- HANDLERS -------------------- */

  const onPageChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newModel) => {
    if (newModel.length) {
      setSortModel(newModel);
    }
  };

  const exportFile = async (type) => {
    if (!payload) return;
    await downloadFile(type, payload);
  };

  const addTo = () =>
    openDialog(
      "add_material",
      "Add Material",
      <AddMaterial
        userId={userId}
        fetchData={() => getAllUserMaterial(payload)}
        selectedRows={selectedRows}
        key={getTimeStamp()}
      />,
    );

  const editTo = (id) =>
    openDialog(
      "edit_material",
      "Edit Material",
      <EditMaterial
        userMaterialId={id}
        userId={userId}
        fetchData={() => getAllUserMaterial(payload)}
        selectedRows={selectedRows}
        key={getTimeStamp()}
      />,
    );

  /* -------------------- RENDER -------------------- */

  return (
    <FormProvider {...methods}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <CustomDataGrid
            trans={t}
            id="id"
            rows={userMaterials}
            rowCount={totalNumberOfRecords}
            isLoading={isLoading}
            sortModel={sortModel}
            handleSortModelChange={handleSortModelChange}
            setSelectedRows={setSelectedRows}
            paginationModel={paginationModel}
            onPaginationModelChange={onPageChange}
            paging={{ pageNumber, pageSize }}
            paginationMeta={paginationMeta}
            addPage={addTo}
            editPage={editTo}
            downloadFile={exportFile}
          />
        </Grid>
      </Grid>
    </FormProvider>
  );
}

export default Materials;
